/**
 * Pricing FAQ (build Appendix A3). Every number is derived from
 * `@shared/pricing`. The annual entry only appears once annual billing is on.
 */
import { isAnnualEnabled } from "@shared/pricing";
import {
  ANNUAL_MONTHS_CHARGED,
  ANNUAL_MONTHS_COVERED,
  MONEY_BACK_DAYS,
  OWNER_PRICE,
  PAUSED_RETENTION_DAYS,
  growthFaqAnswer,
} from "@/lib/marketing/pricing-copy";
import { TERMS_PRICING_HREF } from "@/lib/marketing/links";

export interface FaqItem {
  question: string;
  answer: string;
  link?: { href: string; label: string };
}

export function pricingFaq(now: Date = new Date()): FaqItem[] {
  const items: FaqItem[] = [
    {
      question: "Does a plan include continuous AI drafting?",
      answer: "AI-drafted replies require beta access. Continuous Auto Draft also requires enabling the feature; signup or a paid plan alone does not grant access. Auto-send defaults to off, emergencies need a person, and quiet hours hold automatic sends.",
    },
    {
      question: "What counts as a property?",
      answer: "One address, on or off PadSplit, with any number of rooms.",
    },
    {
      question: "How does the price change as I grow?",
      answer: growthFaqAnswer(12),
    },
    {
      question: "What happens after the trial?",
      answer: `Add a card anytime to keep going. If you don't, your account pauses as read-only and we keep your data for ${PAUSED_RETENTION_DAYS} days.`,
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. Monthly plans can be cancelled anytime, and you won't be charged again.",
    },
    {
      question: "Do you offer refunds?",
      answer: `Annual and lifetime purchases come with a ${MONEY_BACK_DAYS}-day money-back guarantee. Monthly plans aren't refunded, but you can cancel anytime.`,
      link: { href: TERMS_PRICING_HREF, label: "Pricing, trials, refunds and lifetime plans" },
    },
    {
      question: "Will my price change?",
      answer:
        "We review prices periodically and give 30 days' notice. Prices never change in the middle of a paid term.",
    },
    {
      question: "Do property managers pay per owner?",
      answer: `Yes, ${OWNER_PRICE} per owner a month, which covers the owner portal and monthly owner statements.`,
    },
  ];
  if (isAnnualEnabled(now)) {
    items.push({
      question: "Is there an annual plan?",
      answer: `Yes. Pay for ${ANNUAL_MONTHS_CHARGED} months and get ${ANNUAL_MONTHS_COVERED}, with your price locked for the year.`,
    });
  }
  return items;
}
